import { Maybe } from '@types';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useWidthCheck } from '../../../../hooks';
import { TABLET_WIDTH } from '../../../../settings';
import { useSwipeable } from 'react-swipeable';
import { Markdown as MD } from '@features/markdown/components/Markdown';

type Props = {
  title: Maybe<string>;
  tagline: Maybe<string>;
  children?: any;
  isActive: boolean;
  onNextClick: any;
  onPrevClick: any;
  listItems: string[][];
  bottomNote?: any;
};

const ItemsListSlide = ({
  title,
  tagline,
  children,
  isActive,
  onNextClick,
  onPrevClick,
  listItems,
  bottomNote,
}: Props) => {
  const isTabletView = useWidthCheck(TABLET_WIDTH);
  const [hidden, setHidden] = useState(!isActive);

  const swipeHandler = useSwipeable({
    onSwipedRight: onPrevClick,
    onSwipedLeft: onNextClick,
    delta: 40,
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: false,
    rotationAngle: 0,
    swipeDuration: Infinity,
    touchEventOptions: { passive: true },
  });

  useEffect(() => {
    let hideTimeoutId = null;
    let showTimeoutId = null;

    if (!isActive && !hidden) {
      hideTimeoutId = setTimeout(() => {
        if (!isActive) setHidden(true);
        hideTimeoutId = null;
      }, 200);
    } else if (isActive && hidden) {
      showTimeoutId = setTimeout(() => {
        setHidden(false);
        showTimeoutId = null;
      }, 200);
    }

    return () => {
      !!hideTimeoutId && clearTimeout(hideTimeoutId);
      !!showTimeoutId && clearTimeout(showTimeoutId);
    };
  }, [isActive]);

  // if (hidden && !isTabletView) return null;

  return (
    <div
      {...swipeHandler}
      className={classNames('tw:animate-[fadeIn_0.8s] tw:px-5 tw:text-left', {
        'tw:hidden': hidden,
        'tw:animate-[fadeOut_0.3s]': !isActive && !hidden,
      })}>
      <div className='tw:mx-auto tw:max-w-[450px] tw:lg:max-w-[950px]'>
        {tagline && !isTabletView && (
          <h3 className='tw:mb-5 tw:font-title tw:font-bold tw:text-neutrals-dark-grey tw:lg:text-2xl'>
            {tagline}
          </h3>
        )}
        {title && (
          <p className='tw:text-3xl tw:font-bold tw:text-brand-black tw:lg:text-[45px] tw:lg:leading-[56px]'>
            {title}
          </p>
        )}

        <div className='tw:mt-2 tw:flex tw:flex-col tw:justify-between tw:lg:mt-10 tw:lg:flex-row'>
          {listItems.map((listGroup, groupIdx) => (
            <div
              style={{ width: isTabletView ? '100%' : `${(1 / listItems.length) * 100}%` }}
              key={groupIdx}
              className='tw:gap-3'>
              <ul className='tw:list-disc tw:pl-5 tw:marker:text-[#ef8114]'>
                {listGroup.map((listItem, listItemIdx) => (
                  <li
                    className='tw:mb-3 tw:max-w-[450px] tw:text-neutrals-dark-grey tw:last:mb-0'
                    key={listItemIdx}>
                    <MD source={listItem} className='tw:text-left' />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {bottomNote && (
          <MD
            source={bottomNote}
            className='tw:mt-5 tw:pl-5 tw:text-left tw:text-sm tw:font-normal tw:text-muted tw:md:text-base'
          />
        )}
        <div className='tw:mt-5 tw:flex tw:justify-center tw:lg:justify-end'>{children}</div>
      </div>
    </div>
  );
};

export default ItemsListSlide;
